import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { HOME } from "@/constants/testIds";
import { SearchAutocomplete } from "@/components/SearchAutocomplete";

export function HomeHero() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const goSearch = (query, extra = {}) => {
    const params = new URLSearchParams();
    const text = (query || "").trim();
    if (text) params.set("q", text);
    Object.entries(extra).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    const qs = params.toString();
    navigate(qs ? `/recherche?${qs}` : "/recherche");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    goSearch(q);
  };

  const onSelectSuggestion = (item) => {
    if (item.action === "category" && item.value) {
      goSearch("", { category: item.value });
      return;
    }
    if (item.action === "subcategory" && item.value) {
      const extra = { subcategory: item.value };
      if (item.category) extra.category = item.category;
      goSearch(item.label, extra);
      return;
    }
    if (item.action === "brand" && item.value) {
      goSearch("", { brand: item.value });
      return;
    }
    goSearch(item.label || item.value || "");
  };

  return (
    <section className="relative z-20 bg-white" data-testid={HOME.hero}>
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden bg-[radial-gradient(ellipse_at_top_left,_rgba(22,163,74,0.10),_transparent_50%),radial-gradient(ellipse_at_bottom_right,_rgba(24,24,27,0.05),_transparent_45%)]"
        aria-hidden
      />
      <div className="matpro-container relative py-10 sm:py-14 lg:py-16">
        <p className="matpass-hero-item text-center text-[11px] font-bold uppercase tracking-[0.22em] text-matpro-green-dark">
          {t("home.badge")}
        </p>
        <h1
          data-testid={HOME.slogan}
          className="matpass-hero-item matpass-hero-delay-1 mt-4 text-center font-heading text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl"
        >
          <span className="text-matpro-anthracite">MAT</span>
          <span className="text-matpro-green">PASS</span>
        </h1>
        <p className="matpass-hero-item matpass-hero-delay-2 mx-auto mt-4 max-w-2xl text-center text-base leading-relaxed text-matpro-anthracite-muted sm:text-lg">
          {t("home.hero_hook")}
        </p>

        <form
          onSubmit={onSubmit}
          className="matpass-hero-item matpass-hero-delay-3 relative z-30 mx-auto mt-8 flex max-w-2xl flex-col gap-3 sm:mt-10 sm:flex-row sm:items-stretch"
          data-testid="home-search-form"
        >
          <SearchAutocomplete
            value={q}
            onChange={setQ}
            onSubmit={() => goSearch(q)}
            onSelectSuggestion={onSelectSuggestion}
            placeholder={t("home.search_placeholder")}
            ariaLabel={t("a11y.search_label")}
            wrapperClassName="flex-1"
            testId="home-search"
          />
          <button
            type="submit"
            className="matpass-btn matpass-icon-hover inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-matpro-green px-6 text-sm font-bold text-white hover:bg-matpro-green-dark"
            data-testid="home-search-cta"
          >
            {t("home.cta_search")}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        </form>
      </div>
    </section>
  );
}
